import { type RadioChangeEvent } from "antd/lib/radio/interface";

import { BuiRadio, BuiRadioGroup } from "@btnet/blueprint-components";

import {
  BdsAnalyticsEvent,
  BdsTrack,
  type IBdsTrackingProps,
  trackBdsEvent,
} from "@buildertrend/components";

import {
  type IBTRadioGroupProps,
  type IBTRadioProps,
} from "./BTRadioGroup.types";

/**
 * BTRadioGroup wraps the Blueprint `BuiRadioGroup` primitive and re-adds the
 * value-change analytics tracking that the primitive intentionally omits.
 *
 * Use `BTRadio` for the individual options (or pass the `options` prop).
 * @example
 * <BTRadioGroup id="contractType" data-testid="contractType" defaultValue="1">
 *   <BTRadio value="1">Fixed price</BTRadio>
 *   <BTRadio value="2">Open book</BTRadio>
 * </BTRadioGroup>
 */
export const BTRadioGroup = BdsTrack({ element: "BTRadioGroup" })(({
  onChange,
  tracking,
  trackingEvent,
  analyticId = "BTRadioGroup",
  "data-testid": testid,
  ...otherProps
}: IBTRadioGroupProps & IBdsTrackingProps) => {
  const handleChange = (e: RadioChangeEvent) => {
    trackBdsEvent(tracking, {
      element: "BTRadioGroup",
      event: trackingEvent ?? BdsAnalyticsEvent.ValueChange,
      analyticId,
      testid,
      value: e.target.value,
    });
    onChange?.(e);
  };

  return (
    <BuiRadioGroup
      {...otherProps}
      onChange={handleChange}
      {...(testid !== undefined ? { "data-testid": testid } : {})}
    />
  );
});

/**
 * BTRadio is an individual radio option used within `BTRadioGroup`. It is a thin
 * passthrough to the Blueprint `BuiRadio` primitive.
 */
export const BTRadio = (props: IBTRadioProps) => <BuiRadio {...props} />;
